import {
    regionalSiteConfig,
    TheWestRegionalSections,
    TheWestSection,
} from '@news-mono/common'
import { metrics } from '@news-mono/component-library'
import {
    AdTargeting,
    getResolvedTopicPageMeta,
    RouteInformation,
    RouteResolution,
    RouteServices,
    StaticRoutes,
    Topic,
} from '@news-mono/web-common'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { PageMetaItemDTO } from '@west-australian-newspapers/publication-types'
import { layout } from '../../../App.routing'
import { getTopicSponsor } from '../../topic/get-topic-sponsor'
import {
    getAflCurationSierraComponent,
    getDefaultAflLayout,
} from './afl-route-component-layouts'
import {
    getFootyLivesHereHeader,
    getFootyLivesHereSocialImageMeta,
} from './footy-lives-here'

const getSocialImageMeta = (topic: Topic): PageMetaItemDTO[] => {
    return [
        {
            property: 'og:image',
            content: '/static/share-images/share-400x400.png?imwidth=1024',
        },
        {
            name: 'twitter:image',
            content: '/static/share-images/share-400x400.png?imwidth=1024',
        },
        {
            property: 'og:image:height',
            content: '400',
        },
        {
            property: 'og:image:width',
            content: '400',
        },
        {
            property: 'og:description',
            content: topic.seoDescription ?? `The Latest in ${topic.title}`,
        },
        {
            property: 'twitter:description',
            content: topic.seoDescription ?? `The Latest in ${topic.title}`,
        },
    ]
}

const defaultTopic: Topic = {
    id: 'sport/afl',
    title: 'AFL',
    seoTitle: 'AFL',
    metadata: {},
    parent: {
        id: 'sport',
        title: 'Sport',
        seoTitle: 'Sport',
        metadata: {},
    },
}

export const getAflRouteInfo: StaticRoutes<TheWestSection> = {
    '/sport/afl': ({ getAdTargeting, config, store, resolution }) => {
        const topic = getResolvedTopicPageMeta(resolution) ?? defaultTopic
        const section = 'default'

        const isFootyLivesHereEnabled = isFeatureEnabled(
            toFeatureState(store.getState().toggles),
            'afl-footy-lives-here-custom-landing-page',
        )

        const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
            'home',
            section,
            topic,
        )
        const adTargeting: AdTargeting = {
            pageId: topic?.title || defaultTopic.title,
            adUnitPath,
            ssAdUnits,
            topics,
        }

        const footyPageHeader = isFootyLivesHereEnabled
            ? getFootyLivesHereHeader(topic ?? defaultTopic)
            : [getAflCurationSierraComponent()]

        return {
            kind: 'page',
            heading: topic?.title || defaultTopic.title,
            section,
            pageType: 'topic',
            adTargeting,
            hideHeading: true,
            includeFromSectionInMetaDescription: !topic?.seoDescription,
            pageMeta: {
                description:
                    topic?.seoDescription ||
                    `The Latest in ${defaultTopic.title}`,
                title: topic?.seoTitle || defaultTopic.title,
                meta: isFootyLivesHereEnabled
                    ? getFootyLivesHereSocialImageMeta(topic)
                    : getSocialImageMeta(topic),
            },
            socialMeta: {},
            compositions: [
                layout.composition({
                    type: 'box',
                    props: {
                        containerWidth:
                            metrics.thewest.siteMetrics.mainContentWidth,
                        horizontalGutters: 'outerMargin',
                        verticalGutters: ['outerMargin', 'unset'],
                    },
                    contentAreas: {
                        main: [
                            ...(isFootyLivesHereEnabled
                                ? []
                                : [
                                      // Can remove breaking-news if needed
                                      layout.component({
                                          type: 'breaking-news',
                                          props: {
                                              dataDefinitionArgs: {
                                                  type: 'curation',
                                                  name: 'breaking-news',
                                                  offset: 0,
                                                  pageSize: 1,
                                              },
                                          },
                                      }),
                                      layout.component({
                                          type: 'breaking-news-promo-strap',
                                          props: {
                                              dataDefinitionArgs: {
                                                  type: 'curation',
                                                  name: 'breaking-news-promo-strap',
                                                  offset: 0,
                                                  pageSize: 1,
                                              },
                                          },
                                      }),
                                  ]),
                            layout.component({
                                type: 'breadcrumb',
                                props: {
                                    topic,
                                    sponsor: getTopicSponsor(topic),
                                    enableHeaderTag: true,
                                },
                            }),
                        ],
                    },
                }),
                layout.composition({
                    type: 'thor',
                    props: {
                        containerWidth:
                            metrics.thewest.siteMetrics.mainContentWidth,
                        horizontalGutters: true,
                    },
                    contentAreas: {
                        main: [...footyPageHeader, ...getDefaultAflLayout()],
                    },
                }),
            ],
        }
    },
}

export const getAflSubscribeRouteInfo: StaticRoutes<TheWestSection> = {
    '/sport/afl/subscribe': (services) => {
        const topic =
            getResolvedTopicPageMeta(services.resolution) ?? defaultTopic
        const isAflFootyLiveHereCampaignEnabled = isFeatureEnabled(
            toFeatureState(services.store.getState().toggles),
            'afl-footy-lives-here-breach-screen',
        )

        if (!isAflFootyLiveHereCampaignEnabled) {
            return {
                kind: 'redirect',
                redirectTo: {
                    targetUrl: `${services.config.publicUrl}/sport/afl`,
                },
            }
        }

        return createAFLSubscribePage(
            services,
            'default',
            'The West Australian',
            topic,
        )
    },
}

export function createAFLSubscribePage(
    services: RouteServices<RouteResolution>,
    section: 'default' | TheWestRegionalSections,
    title: string,
    topic: Topic,
): RouteInformation<TheWestSection> {
    const canonicalUrl =
        (section === 'default'
            ? services.config.publicUrl
            : regionalSiteConfig[section].publicUrl) + '/sport/afl/subscribe'
    const socialImage = require('./footy-lives-here/assets/2026_WA-Footy-Lives-Here-Social.jpg')

    return {
        kind: 'page',
        heading: `Subscribe to ${title}`,
        pageType: 'breach',
        hideHeading: true,
        refreshOnInactive: false,
        pageMeta: {
            title: `Subscribe to ${title}`,
            link: [{ rel: 'canonical', href: canonicalUrl }],
            meta: [
                {
                    property: 'og:image',
                    content: socialImage,
                },
                {
                    property: 'og:image:width',
                    content: '1920',
                },
                {
                    property: 'og:image:height',
                    content: '1080',
                },
                { name: 'twitter:image', content: socialImage },
                {
                    property: 'og:description',
                    content: `Subscribe to ${title}: Subscription packages for ${title} including print delivery and digital bundles.`,
                },
                {
                    property: 'twitter:description',
                    content: `Subscribe to ${title}: Subscription packages for ${title} including print delivery and digital bundles.`,
                },
                {
                    property: 'robots',
                    content: 'noindex',
                },
            ],
        },
        adTargeting: services.getAdTargeting('home', 'default', 'default'),
        socialMeta: {
            title: `Subscribe to ${title}`,
            description: `Subscribe to ${title}: Subscription packages for ${title} including print delivery and digital bundles.`,
        },
        section: section,
        compositions: [
            layout.composition({
                type: 'thor',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                    horizontalGutters: true,
                    verticalGutters: false,
                    stretchSelf: true,
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                verticalSpacing: 'unset',
                            },
                            contentAreas: {
                                main: [
                                    layout.component({
                                        type: 'redirector',
                                        props: {
                                            getRedirectPathFromState: () =>
                                                '/sport/afl',
                                            isStaticRedirect: true,
                                        },
                                        requiresAccess: {
                                            level: 'subscriber',
                                        },
                                    }),
                                    layout.component({
                                        type: 'breachscreen',
                                        props: {
                                            title: `Subscribe to ${title}`,
                                            breachDeal:
                                                'afl-footy-lives-here-breach-screen',
                                            breachType:
                                                'afl-footy-lives-here-breach-screen',
                                            breachCreativeName:
                                                'afl-footy-lives-here-breach-screen',
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ],
    }
}
